import { useEffect, useState } from "react";
import { Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { useBrand, applyColors } from "@/lib/brand";
import { api, errMsg, fileUrl } from "@/lib/api";
import { Logo } from "@/components/site/Logo";

const DEF = { primary_color: "#2B8048", accent_color: "#C8402F" };

const ColorField = ({ label, value, onChange, testId }) => (
  <label className="flex items-center gap-4 border border-white/10 p-4">
    <input data-testid={`${testId}-picker`} type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-12 w-12 cursor-pointer border-0 bg-transparent p-0" />
    <span className="flex-1">
      <span className="block font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">{label}</span>
      <input data-testid={`${testId}-hex`} value={value} onChange={(e) => onChange(e.target.value)} maxLength={7} className="mt-1 w-full bg-transparent font-mono text-bone focus:outline-none" />
    </span>
  </label>
);

export const BrandingTab = () => {
  const { settings, setSettings } = useBrand();
  const [primary, setPrimary] = useState(settings.primary_color);
  const [accent, setAccent] = useState(settings.accent_color);
  const [name, setName] = useState(settings.company_name);
  const [busy, setBusy] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);

  useEffect(() => {
    applyColors(primary, accent);
  }, [primary, accent]);

  const save = async (body) => {
    setBusy(true);
    try {
      const { data } = await api.put("/settings", body);
      setSettings((s) => ({ ...s, ...data }));
      toast.success("Branding saved — live on your website");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const uploadLogo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setLogoBusy(true);
    try {
      const { data } = await api.post("/settings/logo", fd);
      setSettings((s) => ({ ...s, ...data }));
      toast.success("Logo updated");
    } catch (err) { toast.error(errMsg(err)); } finally { setLogoBusy(false); e.target.value = ""; }
  };

  return (
    <div data-testid="branding-tab" className="grid gap-8 lg:grid-cols-2">
      <section className="space-y-5 border border-white/10 bg-coal p-6">
        <h2 className="font-display text-lg font-extrabold uppercase">Your logo</h2>
        <div className="grid min-h-[120px] place-items-center border border-white/10 bg-ink p-6"><Logo testId="branding-logo-preview" /></div>
        <label className="flex cursor-pointer items-center justify-center gap-2 border border-dashed border-white/20 py-4 text-sm text-zinc-300 hover:border-brand">
          {logoBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4 text-brand" />} Upload logo (PNG or SVG with transparent background works best)
          <input data-testid="branding-logo-input" type="file" accept="image/*" className="hidden" onChange={uploadLogo} />
        </label>
        {settings.logo_path && (
          <div className="flex items-center justify-between text-xs text-zinc-400">
            <a href={fileUrl(settings.logo_path)} target="_blank" rel="noreferrer" className="hover:text-brand">View current file</a>
            <button data-testid="branding-logo-remove" onClick={() => save({ remove_logo: true })} className="hover:text-red-400">Remove logo</button>
          </div>
        )}
        <label className="block">
          <span className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">Company name</span>
          <input data-testid="branding-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-2 w-full border border-white/15 bg-transparent px-3 py-2.5 text-bone focus:border-brand focus:outline-none" />
        </label>
      </section>
      <section className="space-y-5 border border-white/10 bg-coal p-6">
        <h2 className="font-display text-lg font-extrabold uppercase">Company colors</h2>
        <ColorField testId="branding-primary" label="Main color (buttons, highlights)" value={primary} onChange={setPrimary} />
        <ColorField testId="branding-accent" label="Second color" value={accent} onChange={setAccent} />
        <div className="flex items-center gap-3 border border-white/10 bg-ink p-5">
          <span className="bg-brand px-4 py-2 font-display text-xs font-bold uppercase text-ink">Preview button</span>
          <span className="font-serif-i text-2xl text-brand">Smartkey</span>
          <span className="ml-auto h-6 w-6 bg-brand2" />
        </div>
        <div className="flex flex-wrap gap-3">
          <button data-testid="branding-save-button" disabled={busy || !/^#[0-9a-f]{6}$/i.test(primary) || !/^#[0-9a-f]{6}$/i.test(accent)} onClick={() => save({ primary_color: primary, accent_color: accent, company_name: name })} className="inline-flex items-center gap-2 bg-brand px-6 py-3 font-display text-sm font-extrabold uppercase tracking-wider text-ink disabled:opacity-50">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Save branding
          </button>
          <button data-testid="branding-reset-button" onClick={() => { setPrimary(DEF.primary_color); setAccent(DEF.accent_color); }} className="border border-white/15 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-300 hover:border-brand">Reset defaults</button>
        </div>
      </section>
    </div>
  );
};
